import { useBranding } from "@/contexts/BrandContext";
import { readCachedBrand } from "@/contexts/brandCache";

interface Props {
  className?: string;
}

// Platform-default brand name, mirrored from AppLogo's `alt` fallback. Used only
// to derive the default monogram when the active brand supplies no name.
const DEFAULT_BRAND_NAME = "DDKSoft Logistic Platform";

/**
 * Derives a short monogram (1–2 uppercase initials) from a brand name.
 *
 * Takes the first letter of up to the first two whitespace-separated words —
 * e.g. "DDKSoft Logistic Platform" -> "DL", "Acme" -> "A". Non-letter leading
 * characters are skipped so a name like "7Bridges" still yields "B". Returns an
 * empty string for a blank name so the caller can fall back.
 */
function brandMonogram(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}]/gu, ""))
    .filter(Boolean);
  const initials = words.slice(0, 2).map((word) => word[0]!.toUpperCase());
  return initials.join("");
}

/**
 * Brand-aware square mark for the COLLAPSED sidebar (BUG-305).
 *
 * Mirrors {@link AppLogo}'s brand resolution but renders a square badge that
 * fits the collapsed `h-14` rail: prefers a square brand asset (favicon, then
 * the dark logo) when the brand provides one, otherwise renders a MONOGRAM
 * derived from the brand name. Falls back to the host-cached brand and finally
 * the platform-default name — so a white-labeled tenant shows ITS mark and the
 * default brand shows the platform monogram, never a hardcoded string.
 */
export function BrandMark({ className }: Props) {
  const branding = useBranding();
  const cached = branding.is_default ? readCachedBrand() : null;

  const squareSrc =
    branding.favicon_url ||
    branding.logo_dark_url ||
    cached?.favicon_url ||
    cached?.logo_dark_url ||
    "";
  const name = branding.brand_name || cached?.brand_name || DEFAULT_BRAND_NAME;
  const monogram = brandMonogram(name) || brandMonogram(DEFAULT_BRAND_NAME);

  if (squareSrc) {
    return (
      <img
        src={squareSrc}
        alt={name}
        className={`block h-7 w-7 rounded object-contain${
          className ? ` ${className}` : ""
        }`}
      />
    );
  }

  return (
    <span
      aria-label={name}
      className={`block text-sm font-black tracking-tight text-white${
        className ? ` ${className}` : ""
      }`}
    >
      {monogram}
    </span>
  );
}
